import { fonts } from '@/theme/fonts';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Images } from '@/assets';
import { PlayerListItemType } from '@/api/type/types';
import { getCountryNameByCode } from '@/utils/name/getCountryName';
import { formatInternationalNumber } from '@/utils/number/formatInternationalNumber';
import { getAddress } from '@/utils/name/getAddress';
import { formatDate } from '@/utils/date-time/formatDate';
import { useTranslation } from 'react-i18next';
import { formatName } from '@/utils/name/formatName';
import CenterBubble from '@/components/bubble/CenterBubble';

interface PlayerListItemProps {
  items: PlayerListItemType[];
}

const PlayerListItem = ({ items }: PlayerListItemProps) => {
  const { i18n, t } = useTranslation();
  const [openChatId, setOpenChatId] = useState<string | null>(null);
  const columns = [
    {
      key: 'contactCountryCode',
      label: t('nation'),
      width: '11%',
      align: 'left',
    },
    { key: 'loginId', label: t('id'), width: '16%', align: 'left' },
    { key: 'name', label: t('name'), width: '11.5%', align: 'left' },
    {
      key: 'clinicName',
      label: t('clinic2'),
      width: '12.5%',
      align: 'left',
    },
    {
      key: 'clinicContact',
      label: t('contact'),
      width: '13%',
      align: 'center',
    },
    { key: 'address', label: t('address'), width: '15%', align: 'left' },
    {
      key: 'firstOrderDate',
      label: t('registration'),
      width: '8.5%',
      align: 'left',
    },
    { key: 'chat', label: t('chat'), width: '11%', align: 'center' },
  ] as const;
  const onMouseEnterChat = (id: string) => setOpenChatId(id);
  const onMouseLeaveChat = () => setOpenChatId(null);

  const valueRenderMap: Record<
    (typeof columns)[number]['key'],
    (row: PlayerListItemType) => React.ReactNode
  > = {
    contactCountryCode: (row) =>
      getCountryNameByCode({
        contactCountryCode: row.clinicContact.contactCountryCode,
        countryCode: row.clinicContact.countryCode,
      }),
    loginId: (row) => row.loginId,
    name: (row) =>
      formatName({
        countryCode: row.clinicContact.contactCountryCode,
        firstName: row.name.firstName,
        lastName: row.name.lastName,
      }),
    clinicName: (row) => row.clinicName,
    clinicContact: (row) =>
      formatInternationalNumber({
        countryCode: row.clinicContact.contactCountryCode,
        number: row.clinicContact.contactNumber,
      }),
    address: (row) =>
      getAddress({
        countryCode: row.clinicContact.contactCountryCode,
        address: {
          address1: row.address.address1,
          address2: row.address.address2,
          postalCode: row.address.postalCode,
          city: row.address.city,
          state: row.address.state,
        },
      }),
    firstOrderDate: (row) => formatDate(row.firstOrderDate, i18n.language),
    chat: (row: PlayerListItemType) => (
      <BubbleSection>
        <ChatIcon
          onMouseEnter={() => onMouseEnterChat(row.id)}
          onMouseLeave={onMouseLeaveChat}
        />
        {openChatId === row.id && (
          <BubbleWrap>
            <CenterBubble desc={t('comingSoon')} />
          </BubbleWrap>
        )}
      </BubbleSection>
    ),
  };

  return (
    <TableWrap>
      <TableElement>
        <Thead>
          <tr>
            {columns.map((col) => (
              <Th key={col.key} $width={col.width} $align={col.align}>
                {col.label}
              </Th>
            ))}
          </tr>
        </Thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id}>
              {columns.map((col) => (
                <Td key={col.key} $align={col.align}>
                  {col.key === 'chat' && valueRenderMap[col.key](row)}
                  {col.key !== 'chat' && (
                    <ValueWrap $align={col.align}>
                      {valueRenderMap[col.key](row)}
                    </ValueWrap>
                  )}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </TableElement>
    </TableWrap>
  );
};

export default PlayerListItem;

const TableWrap = styled.div`
  width: 100%;
  flex: 1;
`;

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  /* max-width: 1170px; */
  box-sizing: border-box;
`;

const Thead = styled.thead`
  background-color: #769bf7;
  text-align: center;
`;

interface ThProps {
  $width: string;
  $align: string;
}

const Th = styled.th<ThProps>`
  font-family: ${fonts.family.poppins};
  font-weight: ${fonts.weight.semiBold};
  font-size: 15px;
  line-height: 120%;
  color: #fcfdff;
  height: 50px;
  padding-left: 18px;
  box-sizing: border-box;
  white-space: nowrap;
  text-align: ${({ $align }) => $align};
  width: ${({ $width }) => $width};
  &:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }
  &:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

interface TdProps {
  $align: string;
}

const Td = styled.td<TdProps>`
  padding: 12px 0 12px 18px;
  text-align: ${({ $align }) => $align};
  border-bottom: 1px solid #769bf7;
  font-family: ${fonts.family.poppins};
  font-size: 14px;
  line-height: 120%;
  color: #000000;
`;

const ChatIcon = styled.button`
  background-image: url(${Images.ChatIcon});
  width: 36px;
  height: 36px;
  transition: all 0.2s ease;
`;

const BubbleSection = styled.div`
  position: relative;
`;

const BubbleWrap = styled.div`
  position: absolute;
  top: 60px;
  left: -37px;
  z-index: 9999;
`;

interface ValueWrapProps {
  $align: string;
}

const ValueWrap = styled.div<ValueWrapProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => $align};
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`;
